export type RealtimeStatus = "connecting" | "open" | "closed" | "idle";

type Handlers = {
  url: () => string | null;
  onMessage: (msg: any) => void;
  onStatus: (s: RealtimeStatus) => void;
  onAuthError: () => Promise<void>;
};

// Auto-reconnecting WebSocket with heartbeat. Server state is re-fetched by the provider on every (re)connect.
export class RealtimeClient {
  private ws: WebSocket | null = null;
  private attempts = 0;
  private retryTimer: any = null;
  private pingTimer: any = null;
  private pongTimer: any = null;
  private stopped = true;

  constructor(private h: Handlers) {}

  start() {
    this.stopped = false;
    this.connect();
  }

  stop() {
    this.stopped = true;
    clearTimeout(this.retryTimer);
    this.clearPing();
    this.ws?.close();
    this.ws = null;
    this.h.onStatus("idle");
  }

  reconnectNow() {
    if (this.stopped || this.ws?.readyState === WebSocket.OPEN) return;
    clearTimeout(this.retryTimer);
    this.attempts = 0;
    this.connect();
  }

  private connect() {
    const url = this.h.url();
    if (!url || this.stopped) return;
    this.h.onStatus("connecting");
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => { this.attempts = 0; this.h.onStatus("open"); this.startPing(); };
    ws.onmessage = (e) => {
      let msg: any;
      try { msg = JSON.parse(e.data); } catch { return; }
      if (msg.type === "pong") { clearTimeout(this.pongTimer); return; }
      this.h.onMessage(msg);
    };
    ws.onerror = () => ws.close();
    ws.onclose = async (e) => {
      if (this.ws !== ws) return;
      this.clearPing();
      this.h.onStatus("closed");
      if (this.stopped) return;
      if (e.code === 4401) await this.h.onAuthError();
      this.scheduleRetry();
    };
  }

  private scheduleRetry() {
    const delay = Math.min(30000, 1000 * 2 ** this.attempts) + Math.random() * 500;
    this.attempts += 1;
    this.retryTimer = setTimeout(() => this.connect(), delay);
  }

  private startPing() {
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState !== WebSocket.OPEN) return;
      this.ws.send(JSON.stringify({ type: "ping", t: Date.now() }));
      clearTimeout(this.pongTimer);
      this.pongTimer = setTimeout(() => this.ws?.close(), 10000);
    }, 25000);
  }

  private clearPing() {
    clearInterval(this.pingTimer);
    clearTimeout(this.pongTimer);
  }
}
